import { useEffect, useRef, type ReactNode, type ElementType } from "react";
import { trackSectionView } from "@/lib/scrollTracking";

interface ScrollSectionProps {
  /** Analytics id for this section, e.g. "hero", "features", "faq". */
  name: string;
  /** Page slug so the same section name can appear on multiple pages. */
  page: string;
  /** Visibility ratio required to count as "viewed". Default 0.5. */
  threshold?: number;
  /** Dwell time (ms) required at threshold before firing. Default 400. */
  dwellMs?: number;
  children: ReactNode;
  className?: string;
  as?: keyof JSX.IntrinsicElements;
}

/**
 * Wraps a marketing section and fires a single `section_viewed` event when
 * the section has been sufficiently visible for the dwell period. Passive —
 * never blocks rendering and never re-fires within the same session.
 */
export function ScrollSection({
  name,
  page,
  threshold = 0.5,
  dwellMs = 400,
  children,
  className,
  as: Tag = "section",
}: ScrollSectionProps) {
  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    let timer: number | undefined;
    const start = performance.now();

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= threshold) {
            if (timer) window.clearTimeout(timer);
            timer = window.setTimeout(() => {
              trackSectionView(page, name, Math.round(performance.now() - start));
              io.disconnect();
            }, dwellMs);
          } else if (timer) {
            window.clearTimeout(timer);
            timer = undefined;
          }
        }
      },
      { threshold: [0, threshold, 1] },
    );
    io.observe(el);
    return () => {
      if (timer) window.clearTimeout(timer);
      io.disconnect();
    };
  }, [name, page, threshold, dwellMs]);

  // JSX generic element: cast via unknown to accept ref for any HTML tag.
  const Component = Tag as unknown as ElementType;
  return (
    <Component ref={ref} className={className} data-scroll-section={name}>
      {children}
    </Component>
  );
}

export default ScrollSection;
